// src/pages/applications/AdminStarter.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../../components/LocalizedLink";

const ASSET = (p) => `${process.env.PUBLIC_URL || ""}${p}`;
const IMG = (f) => ASSET(`/adminstarter/${f}`);

export default function AdminStarter() {
  const { t } = useTranslation();
  const bullets =
    t("applications.detail.adminstarter.hero.bullets", {
      returnObjects: true,
    }) || [];
  const features = ["rbac", "crud", "charts", "audit", "import", "api"];
  const screens = [
    [
      "screenshot-dashboard.svg",
      "applications.detail.adminstarter.screens.captions.dashboard",
    ],
    [
      "screenshot-table.svg",
      "applications.detail.adminstarter.screens.captions.table",
    ],
    [
      "screenshot-form.svg",
      "applications.detail.adminstarter.screens.captions.form",
    ],
    [
      "screenshot-audit.svg",
      "applications.detail.adminstarter.screens.captions.audit",
    ],
  ];

  const plans = {
    starter: t("applications.detail.adminstarter.pricing.items.starter", {
      returnObjects: true,
    }),
    standard: t("applications.detail.adminstarter.pricing.items.standard", {
      returnObjects: true,
    }),
    plus: t("applications.detail.adminstarter.pricing.items.plus", {
      returnObjects: true,
    }),
  };

  return (
    <section className="py-16">
      {/* Hero */}
      <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800 ring-1 ring-indigo-100">
            {t("applications.detail.adminstarter.hero.badge_left")}
            <span className="inline-block h-1 w-1 rounded-full bg-indigo-400" />
            {t("applications.detail.adminstarter.hero.badge_right")}
          </span>

          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
            {t("applications.detail.adminstarter.hero.title")}
          </h1>
          <p className="mt-4 text-lg text-slate-800">
            {t("applications.detail.adminstarter.hero.subtitle")}
          </p>
          <ul className="mt-6 space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-800">
                <span className="mt-2 h-2.5 w-2.5 rounded-full bg-indigo-600" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <a
              href="/adminstarter/cover.svg"
              className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-500"
            >
              {t("applications.detail.adminstarter.hero.view_demo")}
            </a>
            <LocalizedLink
              to="/contact"
              className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
            >
              {t("applications.detail.adminstarter.hero.start")}
            </LocalizedLink>
          </div>
        </div>
        <div className="relative">
          <img
            src={ASSET("/adminstarter/cover.svg")}
            alt="Admin Starter cover"
            className="rounded-2xl shadow-2xl ring-1 ring-black/10"
          />
        </div>
      </div>

      {/* Features */}
      <div className="mx-auto max-w-7xl px-4 mt-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((k) => (
            <div
              key={k}
              className="rounded-2xl p-6 border border-slate-200 bg-white shadow-sm"
            >
              <img
                src={IMG(`feature-${k}.svg`)}
                alt=""
                className="h-8 w-8"
                loading="lazy"
              />
              <h3 className="font-semibold text-slate-900">
                {t(`applications.detail.adminstarter.features.${k}.title`)}
              </h3>
              <p className="mt-2 text-sm leading-6 text-slate-700">
                {t(`applications.detail.adminstarter.features.${k}.desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Screens */}
      <div className="mx-auto max-w-7xl px-4 mt-16">
        <div className="grid md:grid-cols-2 gap-6">
          {screens.map(([file, key]) => (
            <figure
              key={file}
              className="rounded-2xl p-4 border border-slate-200 bg-slate-50"
            >
              <img
                src={IMG(file)}
                alt=""
                className="rounded-lg w-full ring-1 ring-black/5"
              />
              <figcaption className="mt-3 text-sm text-slate-700">
                {t(key)}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div className="mx-auto max-w-7xl px-4 mt-16">
        <h2 className="text-2xl font-bold text-slate-900">
          {t("applications.detail.adminstarter.pricing.heading")}
        </h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([k, plan]) => (
            <div
              key={k}
              className="rounded-2xl p-6 border border-slate-200 bg-white shadow-sm"
            >
              <h3 className="font-semibold text-slate-900">{plan.name}</h3>
              <div className="text-xl mt-1 text-slate-900">{plan.price}</div>
              <ul className="mt-4 space-y-2 text-sm">
                {(plan.includes || []).map((it, i) => (
                  <li key={i} className="flex gap-2 text-slate-800">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-indigo-600" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
